<template>
  <!-- .app-header -->
  <header class="app-header" :class="[{ '`skip-links': accessibilityLinks }]">
    <div class="app-header-consent background-2-100" v-if="getConsentRequired === 'true' && hideConsent === false">
      <div class="app-header-consent-container">
        <div class="app-header-consent-content">
          You are yet to give consent for processing your data.
          Please consent now so you can have access to all website functionalities.
          You can always read more about this in our
          <router-link :to="{name: 'privacy-policy'}">Privacy Policy</router-link>.
        </div>
        <div class="app-header-consent-control">
          <button class="background-1-50 color-2-100" type="button" @click.prevent="updateConsent()">I Consent</button>
          <button class="background-2-16 color-2-100" type="button" @click.prevent="discardConsent()">Not now</button>
        </div>
      </div>
    </div>
    <div class="app-header-consent background-2-100" v-if="getImpersonatingUser !== false">
      <div class="app-header-consent-container">
        <div class="app-header-consent-content">
          You are currently emulating
          <template v-if="this.impersonate">
            <template v-if="this.impersonate.role">
              {{ this.impersonate.role }} user
            </template>
            <template v-if="this.impersonate.firstName">
              {{ this.impersonate.firstName }}
            </template>
            <template v-if="this.impersonate.lastName">
              {{ this.impersonate.lastName }}
            </template>
          </template>
           - {{ getImpersonatingUser }}.
          Please remember that all your actions will be reflected in the impersonated user account.
        </div>
        <div class="app-header-consent-control">
          <button class="background-1-50 color-2-100" type="button" @click.prevent="removeImpersonate()" style="width: auto; padding: 0 10px;">Revert to your account</button>
        </div>
      </div>
    </div>

    <template v-if="!this.paid && (getUserRole === 'ca' || getUserRole === 'ta' || getUserRole === 'la') && accountExpiring && accountExpiring <= 30 && !hasActiveSubscription">
      <div class="company-warning-message"
           :class="[{ error: accountExpiring < 0 }]">
        <div class="company-warning-message--container">
          <div></div>
          <div class="company-warning-message--text">
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="triangle-exclamation" class="svg-inline--fa fa-triangle-exclamation" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path fill="currentColor" d="M506.3 417l-213.3-364c-16.33-28-57.54-28-73.98 0l-213.2 364C-10.59 444.9 9.849 480 42.74 480h426.6C502.1 480 522.6 445 506.3 417zM232 168c0-13.25 10.75-24 24-24S280 154.8 280 168v128c0 13.25-10.75 24-23.1 24S232 309.3 232 296V168zM256 416c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 401.9 273.4 416 256 416z"></path>
            </svg>
            <template v-if="accountExpiring < 0">
              Your subscription expired on {{ accountExpiringDate }}. Please renew it now to avoid losing access to your Clear Assured account.
              <template v-if="profile && profile.company && profile.company.activation_step === 5">
                <router-link :to="{name: 'billing-profile'}">Go to your billing profile</router-link>.
              </template>
              <template v-else>
                <router-link :to="{name: 'renew-subscription'}">Go to your billing profile</router-link>.
              </template>
            </template>
            <template v-else>
              Your subscription will expire on {{ accountExpiringDate }}. Please renew it quickly to avoid losing access to you Clear Assured account.
              <template v-if="profile && profile.company && profile.company.activation_step === 5">
                <router-link :to="{name: 'billing-profile'}">Go to your billing profile</router-link>.
              </template>
              <template v-else>
                <router-link :to="{name: 'renew-subscription'}">Go to your billing profile</router-link>.
              </template>
            </template>
          </div>
          <div class="company-warning-message--action">
            <button v-on:click.prevent="accountExpiring = null">
              <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="xmark" class="svg-inline--fa fa-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path fill="currentColor" d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
              </svg>
              Ok, close this</button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="getUserRole === 'ca' && accountExpiring && accountExpiring <= 0 && hasActiveSubscription">
      <div class="company-warning-message error">
        <div class="company-warning-message--container">
          <div></div>
          <div class="company-warning-message--text">
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="triangle-exclamation" class="svg-inline--fa fa-triangle-exclamation" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path fill="currentColor" d="M506.3 417l-213.3-364c-16.33-28-57.54-28-73.98 0l-213.2 364C-10.59 444.9 9.849 480 42.74 480h426.6C502.1 480 522.6 445 506.3 417zM232 168c0-13.25 10.75-24 24-24S280 154.8 280 168v128c0 13.25-10.75 24-23.1 24S232 309.3 232 296V168zM256 416c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 401.9 273.4 416 256 416z"></path>
            </svg>
            We are yet to receive your payment for the last invoice. Please confirm your payment to avoid losing access to your Clear Assured account. <router-link :to="{name: 'billing-profile'}">Go to your billing profile</router-link>.
          </div>
          <div class="company-warning-message--action">
            <button v-on:click.prevent="accountExpiring = null">
              <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="xmark" class="svg-inline--fa fa-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path fill="currentColor" d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
              </svg>
              Ok, close this</button>
          </div>
        </div>
      </div>
    </template>

    <!-- .app-header-main -->
    <div class="app-header-main">
      <!-- .myContainer -->
      <div class="admin-header">
        <!-- .app-header-main__container -->
        <div class="app-header-main__container">
          <!--<a href="/" class="main-logo" tabindex="-1">-->
          <router-link :to="{name: userDashboard}" title="Clear Assured dashboard"
                       class="site-logo"
                       v-bind:style="{backgroundImage: this.customLogo ? 'url(' + getFullServerPath(this.customLogo) + ')' : '' }"
                       :class="this.customLogo ? 'industry-logo' : ''">
          </router-link>
          <div class="skip-to-links" :class="[{ 'active': accessibilityLinks }]">
            <div class="myContainer">
              <a href="#"
                 role="button"
                 v-on:focus="accessibilityLinks = true"
                 v-on:blur="accessibilityLinks = false"
                 v-if="hasSection('sticky-anchors')"
                 @click.prevent="skipTo('sticky-anchors')">Skip to content menu</a>
              <a href="#"
                 v-on:focus="accessibilityLinks = true"
                 v-on:blur="accessibilityLinks = false"
                 v-if="hasSection('admin-gm-breadcrumbs')"
                 @click.prevent="skipTo('admin-gm-breadcrumbs')">Skip to app menu</a>
              <a href="#"
                 v-if="isPage(['user-app'])"
                 v-on:focus="accessibilityLinks = true"
                 v-on:blur="accessibilityLinks = false"
                 @click.prevent="skipTo('chart-accessibility-table')">Skip to the company progress chart</a>
            </div>
          </div>
          <!--</a>-->
          <!-- .app-header-description -->
          <div class="app-header-description" v-if="getUserRole === 'gm'">
            <ul class="main-menu-header">
              <li class="dashboard-icon" v-on:focus="activeSection = ''">
                <router-link :to="{name: 'admin'}" class="parent-item" v-on:focus="activeSection = ''">Dashboard</router-link>
              </li>
              <li class="tasks-icon" v-on:focus="activeSection = ''">
                <router-link :to="{name: 'company-tasks', params: { companyId: 2}}"
                             class="parent-item"
                             v-on:focus="activeSection = ''">Tasks</router-link>
              </li>
              <li class="notifications-icon has-children"
                  :class="[{ 'no-icon': this.unreadNotifications, 'active': activeSection === 'notifications' }]">
                <button class="parent-item"
                        @click="activeSection === 'notifications' ? activeSection = '' : activeSection = 'notifications'"
                        v-bind:aria-expanded="activeSection === 'notifications' ? 'true' : 'false'"
                        aria-controls="menuNotifications">
                  <span class="unread-notifications" v-if="this.unreadNotifications">
                    {{ this.unreadNotifications }}
                  </span>
                  Notifications
                </button>
                <ul class="first-level-menu-header notifications" id="menuNotifications">
                  <li class="dropdown-info-header">
                    <a href="#" @click.prevent="markNotificationsRead()">Mark all as read</a>
                  </li>
                  <li class="dropdown-info-list">
                    <ul class="dropdown-info-group" v-for="item in notifications">
                      <li class="dropdown-info-header">
                        <span class="meta-date">{{ getTodayDate(item.date) }}</span>
                      </li>
                      <li class="notification-item"
                          @click.prevent="readNotification(child)"
                          :class="[{ 'unread': !child.isRead }]"
                          v-for="child in item.children">
                        <template v-if="child.type === 1">
                          <router-link v-if="child.contentId && child.companyId"
                                       :to="{ name: 'company-task', params: { companyId: child.companyId, id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 2 || child.type === 3">
                          <router-link v-if="child.userFromId"
                                       :to="{ name: 'edit-user', params: { id: child.userFromId } }">{{ child.message }}</router-link>
                          <router-link v-if="!child.userFromId"
                                       :to="{ name: 'consent-logs' }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 4">
                          <router-link v-if="child.contentId"
                                       :to="{ name: 'content', params: { id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 5">
                          <router-link v-if="getUserRole === 'gm'"
                                       :to="{ name: 'company-task', params: { companyId: child.companyId, id: child.contentId } }">{{ child.message }}</router-link>
                          <router-link v-else
                                       :to="{ name: 'task', params: { id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else>
                          {{ child.message }}
                        </template>
                      </li>
                    </ul>
                  </li>
                  <li class="see-all">
                    <router-link :to="{ name: 'notifications' }">See all notifications</router-link>
                  </li>
                </ul>
              </li>
              <li class="content-icon has-children" :class="{'active': activeSection === 'content'}">
                <button class="parent-item"
                        @click="activeSection === 'content' ? activeSection = '' : activeSection = 'content'"
                        v-bind:aria-expanded="activeSection === 'content' ? 'true' : 'false'"
                        aria-controls="menuContent">Content</button>
                <ul class="first-level-menu-header" id="menuContent">
                  <li>
                    <router-link :to="{name: 'todo-list'}">Tasks</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'chapters'}">Task categories</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'articles-list'}">Resources</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'categories'}">Resource categories</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'tags'}">Topics</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'industries'}">Industries</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'edit-pages-list'}">Landing pages</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'default-email-templates'}">Email templates</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'dashboard-promotions'}">Dashboard promotions</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'redirects'}">Redirects</router-link>
                  </li>
                </ul>
              </li>
              <li class="users-icon has-children" :class="{'active': activeSection === 'users'}">
                <button class="parent-item"
                        @click="activeSection === 'users' ? activeSection = '' : activeSection = 'users'"
                        v-bind:aria-expanded="activeSection === 'users' ? 'true' : 'false'"
                        aria-controls="menuUsers">Users</button>
                <ul class="first-level-menu-header" id="menuUsers">
                  <li>
                    <router-link :to="{name: 'users'}">Users</router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'companies'}">Companies</router-link>
                  </li>
                </ul>
              </li>
              <li class="logs-icon" v-on:focus="activeSection = ''">
                <router-link :to="{name: 'logs'}" class="parent-item" v-on:focus="activeSection = ''">Logs</router-link>
              </li>
              <!--<li>-->
              <!--<router-link :to="{name: 'security-configuration'}">Security Configuration</router-link>-->
              <!--</li>-->
            </ul>
          </div>
          <div class="front-header-tagline"
               v-bind:aria-label="'In association with: ' + industriesAlts"
               v-if="getUserRole !== 'gm'">
            <div class="front-header-tagline_text background-1-100" aria-hidden="true">
              <template v-if="this.customLogo">Powered by</template>
              <template v-else>In association with</template>
            </div>
            <div class="industry-logos" aria-hidden="true">
              <template v-if="this.customLogo">
                <router-link to="/" title="The Clear Company logo" class="front-header-logo">
                  <img class="clearLogo" src="../../assets/img/svg/logo.svg" alt="The Clear Company logo" />
                </router-link>
              </template>
              <template v-else>
                <template v-for="(row, index) in sponsors">
                  <template v-if="row.url">
                    <a :href="row.url"
                       v-bind:aria-label="row.altImage"
                       target="_blank" class="industry-logos__item"
                       :class="'industry-logos__item__' + (index + 1)" v-if="index < 10">
                      <img class="sponsorLogo" :src="getImageFullPath(row.logo)" alt=" " />
                    </a>
                  </template>
                  <template v-else>
                    <a href="#"
                       v-bind:aria-label="row.altImage"
                       class="industry-logos__item"
                       @click.prevent=""
                       :class="'industry-logos__item__' + (index + 1)" v-if="index < 10">
                      <img class="sponsorLogo" :src="getImageFullPath(row.logo)" alt=" " />
                    </a>
                  </template>
                </template>
              </template>
            </div>

          </div>
          <!-- END:.app-header-description -->
        </div>
        <!-- END:.app-header-main__container -->
      </div>
      <!-- END:.myContainer -->
    </div>
    <!-- END:.app-header-main -->
    <!-- .app-header-info -->
    <div class="app-header-info background-1-20">
      <!-- .myContainer -->
      <div class="admin-header">
        <!-- .app-header-info__container -->
        <div class="app-header-info__container">
          <div class="app-header-menus">
            <!-- .app-header-login -->
            <button class="mobile-menu-trigger background-1-100-after"
                    :class="[{'active': this.mobileMenuShown}]"
                    @click.prevent.stop="openSubmenu($event, 'mobileMenu')">
              <div class="mobile-menu-trigger__icon">
                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="bars" class="svg-inline--fa fa-bars fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path fill="currentColor" d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"></path>
                </svg>
              </div>
              <span>Menu</span>
              <div class="mobile-menu-trigger__arrow">
                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-down" class="svg-inline--fa fa-chevron-down fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
                </svg>
              </div>
            </button>
            <ul class="app-header-login main-menu-regular" :class="[{'mobile-active': this.mobileMenuShown}]">
              <li v-if="isRegularUser">
                <router-link class="menu-link" to="/dashboard">
                  <span class="menu-icon dashboard" :class="[{'color-1-80': !activePage('/dashboard'), 'background-1-100': activePage('/dashboard')}]">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="home" class="svg-inline--fa fa-home fa-w-18" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                      <path fill="currentColor" d="M280.37 148.26L96 300.11V464a16 16 0 0 0 16 16l112.06-.29a16 16 0 0 0 15.92-16V368a16 16 0 0 1 16-16h64a16 16 0 0 1 16 16v95.64a16 16 0 0 0 16 16.05L464 480a16 16 0 0 0 16-16V300L295.67 148.26a12.19 12.19 0 0 0-15.3 0zM571.6 251.47L488 182.56V44.05a12 12 0 0 0-12-12h-56a12 12 0 0 0-12 12v72.61L318.47 43a48 48 0 0 0-61 0L4.34 251.47a12 12 0 0 0-1.6 16.9l25.5 31A12 12 0 0 0 45.15 301l235.22-193.74a12.19 12.19 0 0 1 15.3 0L530.9 301a12 12 0 0 0 16.9-1.6l25.5-31a12 12 0 0 0-1.7-16.93z"></path>
                    </svg>
                  </span>
                  Dashboard
                </router-link>
              </li>
              <li v-if="isRegularUser || isLightUser">
                <router-link class="menu-link" :to="{ name: 'knowledge-base' }">
                  <span class="menu-icon knowledge-base" :class="[{'color-1-80': !activePage('/knowledge-base'), 'background-1-100': activePage('/knowledge-base')}]">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="book" class="svg-inline--fa fa-book fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                      <path fill="currentColor" d="M448 360V24c0-13.3-10.7-24-24-24H96C43 0 0 43 0 96v320c0 53 43 96 96 96h328c13.3 0 24-10.7 24-24v-16c0-7.5-3.5-14.3-8.9-18.7-4.2-15.4-4.2-59.3 0-74.7 5.4-4.3 8.9-11.1 8.9-18.6zM128 134c0-3.3 2.7-6 6-6h212c3.3 0 6 2.7 6 6v20c0 3.3-2.7 6-6 6H134c-3.3 0-6-2.7-6-6v-20zm0 64c0-3.3 2.7-6 6-6h212c3.3 0 6 2.7 6 6v20c0 3.3-2.7 6-6 6H134c-3.3 0-6-2.7-6-6v-20zm253.4 250H96c-17.7 0-32-14.3-32-32 0-17.6 14.4-32 32-32h285.4c-1.9 17.1-1.9 46.9 0 64z"></path>
                    </svg>
                  </span>
                  Inclusion hub
                </router-link>
              </li>
              <li v-if="isRegularUser">
                <router-link class="menu-link" :to="{ name: 'tasks' }">
                  <span class="menu-icon tasks" :class="[{'color-1-80': !activePage('/tasks'), 'background-1-100': activePage('/tasks')}]">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="stream" class="svg-inline--fa fa-stream fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path fill="currentColor" d="M16 128h416c8.84 0 16-7.16 16-16V48c0-8.84-7.16-16-16-16H16C7.16 32 0 39.16 0 48v64c0 8.84 7.16 16 16 16zm480 80H80c-8.84 0-16 7.16-16 16v64c0 8.84 7.16 16 16 16h416c8.84 0 16-7.16 16-16v-64c0-8.84-7.16-16-16-16zm-64 176H16c-8.84 0-16 7.16-16 16v64c0 8.84 7.16 16 16 16h416c8.84 0 16-7.16 16-16v-64c0-8.84-7.16-16-16-16z"></path>
                    </svg>
                  </span>
                  Tasks
                </router-link>
              </li>
              <li v-if="isRegularUser || isLightUser"
                  class="notifications-icon has-children"
                  :class="[{ 'no-icon': this.unreadNotifications, 'active border-1-70': activeSection === 'notifications' }]">
                <button class="parent-item menu-link background-1-70-after"
                        @click.stop="activeSection === 'notifications' ? activeSection = '' : activeSection = 'notifications'"
                        v-bind:aria-expanded="activeSection === 'notifications' ? 'true' : 'false'"
                        aria-controls="menuNotifications">
                  <span class="menu-icon notifications">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="bell" class="svg-inline--fa fa-bell fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                      <path fill="currentColor" d="M224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64zm215.39-149.71c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71z"></path>
                    </svg>
                  </span>
                  <span class="unread-notifications" v-if="this.unreadNotifications">
                    {{ this.unreadNotifications }}
                  </span>
                  Notifications
                </button>
                <ul class="first-level-menu-header notifications border-1-70"
                    :class="[{'active': activeSection === 'notifications'}]"
                    id="menuNotifications">
                  <li class="dropdown-info-header"
                      v-if="notifications && notifications.length"
                      :class="{'no-notifications': !unreadNotifications}">
                    <a href="#" @click.prevent="markNotificationsRead()"
                       v-if="unreadNotifications">
                      Mark all as read
                    </a>
                  </li>
                  <li class="dropdown-info-list" v-if="notifications && notifications.length">
                    <ul class="dropdown-info-group" v-for="item in notifications">
                      <li class="dropdown-info-header">
                        <span class="meta-date">{{ getTodayDate(item.date) }}</span>
                      </li>
                      <li class="notification-item"
                          @click.prevent="readNotification(child)"
                          :class="[{ 'unread': !child.isRead }]"
                          v-for="child in item.children">
                        <template v-if="child.type === 1">
                          <router-link v-if="child.contentId && child.companyId"
                                       :to="{ name: 'company-task', params: { companyId: child.companyId, id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 2 || child.type === 3">
                          <router-link v-if="child.userFromId"
                                       :to="{ name: 'edit-user', params: { id: child.userFromId } }">{{ child.message }}</router-link>
                          <router-link v-if="!child.userFromId"
                                       :to="{ name: 'consent-logs' }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 4">
                          <router-link v-if="child.contentId"
                                       :to="{ name: 'content', params: { id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else-if="child.type === 5">
                          <router-link v-if="getUserRole === 'gm'"
                                       :to="{ name: 'company-task', params: { companyId: child.companyId, id: child.contentId } }">{{ child.message }}</router-link>
                          <router-link v-else
                                       :to="{ name: 'task', params: { id: child.contentId } }">{{ child.message }}</router-link>
                        </template>
                        <template v-else>
                          {{ child.message }}
                        </template>
                      </li>
                    </ul>
                  </li>
                  <li class="see-all border-1-70"  v-if="notifications && notifications.length">
                    <router-link :to="{ name: 'dashboard-notifications' }">See all notifications</router-link>
                  </li>
                  <li class="dropdown-info-list-empty" v-if="!notifications || !notifications.length">
                    There are no notifications.
                  </li>
                </ul>
              </li>
              <li v-if="isRegularUser || isLightUser" class="notifications-icon notifications-icon-mobile">
                <router-link class="menu-link" :to="{name: 'dashboard-notifications'}">
                  <span class="menu-icon notifications">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="bell" class="svg-inline--fa fa-bell fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                      <path fill="currentColor" d="M224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64zm215.39-149.71c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71z"></path>
                    </svg>
                  </span>
                  <span class="unread-notifications" v-if="this.unreadNotifications">
                    {{ this.unreadNotifications }}
                  </span>
                  Notifications
                </router-link>
              </li>
              <li v-if="isRegularUser || isLightUser"
                  class="has-dropdown"
                  :class="[{ 'opened border-1-70': menuShown === 'myCompany', 'background-1-10': mobileMenuShown && menuShown === 'myCompany' && !getDeviceType('desktop') }]"
                  @click.stop="">
                <a href="#"
                   title="Open menu"
                   class="color-2-100 background-1-70-after"
                   @click.prevent="openSubmenu($event, 'myCompany')">
                  <span class="app-header-login-icon-before sf-color-1-60">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="briefcase" class="svg-inline--fa fa-briefcase fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path class="st0" d="M320 336c0 8.84-7.16 16-16 16h-96c-8.84 0-16-7.16-16-16v-48H0v144c0 25.6 22.4 48 48 48h416c25.6 0 48-22.4 48-48V288H320v48zm144-208h-80V80c0-25.6-22.4-48-48-48H176c-25.6 0-48 22.4-48 48v48H48c-25.6 0-48 22.4-48 48v80h512v-80c0-25.6-22.4-48-48-48zm-144 0H192V96h128v32z">
                    </path>
                    </svg>
                  </span>
                  <span class="menu-text">My organisation</span>
                  <span class="app-header-login-icon-after sf-color-1-60">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-down" class="svg-inline--fa fa-chevron-down fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                      <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
                    </svg>
                  </span>
                </a>
                <ul class="app-header-login__dropdown border-1-70">
                  <li v-if="isRegularUser">
                    <router-link :to="{name: 'company-profile'}"
                                 class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                      <div class="app-header-login__item--icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16">
                          <path fill="currentColor" d="M13.5,14.5a.442.442,0,0,1,.5.5v.5a.442.442,0,0,1-.5.5H8V12.5a.442.442,0,0,0-.5-.5h-1a.442.442,0,0,0-.5.5V16H.5a.442.442,0,0,1-.5-.5V15a.442.442,0,0,1,.5-.5H1V.75A.723.723,0,0,1,1.219.219.723.723,0,0,1,1.75,0h10.5a.723.723,0,0,1,.531.219A.723.723,0,0,1,13,.75V14.5ZM6,9.5v-1A.442.442,0,0,0,5.5,8h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,6,9.5Zm0-3v-1A.442.442,0,0,0,5.5,5h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,6,6.5Zm0-3v-1A.442.442,0,0,0,5.5,2h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,6,3.5Zm4,6v-1A.442.442,0,0,0,9.5,8h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,10,9.5Zm0-3v-1A.442.442,0,0,0,9.5,5h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,10,6.5Zm0-3v-1A.442.442,0,0,0,9.5,2h-1a.442.442,0,0,0-.5.5v1a.442.442,0,0,0,.5.5h1A.442.442,0,0,0,10,3.5Z"/>
                        </svg>
                      </div>
                      Organisation profile
                    </router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'account-settings'}"
                                 class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                      <div class="app-header-login__item--icon">
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="user-cog" class="svg-inline--fa fa-user-cog fa-w-20" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                          <path fill="currentColor" d="M610.5 373.3c2.6-14.1 2.6-28.5 0-42.6l25.8-14.9c3-1.7 4.3-5.2 3.3-8.5-6.7-21.6-18.2-41.2-33.2-57.4-2.3-2.5-6-3.1-9-1.4l-25.8 14.9c-10.9-9.3-23.4-16.5-36.9-21.3v-29.8c0-3.4-2.4-6.4-5.7-7.1-22.3-5-45-4.8-66.2 0-3.3.7-5.7 3.7-5.7 7.1v29.8c-13.5 4.8-26 12-36.9 21.3l-25.8-14.9c-2.9-1.7-6.7-1.1-9 1.4-15 16.2-26.5 35.8-33.2 57.4-1 3.3.4 6.8 3.3 8.5l25.8 14.9c-2.6 14.1-2.6 28.5 0 42.6l-25.8 14.9c-3 1.7-4.3 5.2-3.3 8.5 6.7 21.6 18.2 41.1 33.2 57.4 2.3 2.5 6 3.1 9 1.4l25.8-14.9c10.9 9.3 23.4 16.5 36.9 21.3v29.8c0 3.4 2.4 6.4 5.7 7.1 22.3 5 45 4.8 66.2 0 3.3-.7 5.7-3.7 5.7-7.1v-29.8c13.5-4.8 26-12 36.9-21.3l25.8 14.9c2.9 1.7 6.7 1.1 9-1.4 15-16.2 26.5-35.8 33.2-57.4 1-3.3-.4-6.8-3.3-8.5l-25.8-14.9zM496 400.5c-26.8 0-48.5-21.8-48.5-48.5s21.8-48.5 48.5-48.5 48.5 21.8 48.5 48.5-21.7 48.5-48.5 48.5zM224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm201.2 226.5c-2.3-1.2-4.6-2.6-6.8-3.9l-7.9 4.6c-6 3.4-12.8 5.3-19.6 5.3-10.9 0-21.4-4.6-28.9-12.6-18.3-19.8-32.3-43.9-40.2-69.6-5.5-17.7 1.9-36.4 17.9-45.7l7.9-4.6c-.1-2.6-.1-5.2 0-7.8l-7.9-4.6c-16-9.2-23.4-28-17.9-45.7.9-2.9 2.2-5.8 3.2-8.7-3.8-.3-7.5-1.2-11.4-1.2h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c10.1 0 19.5-3.2 27.2-8.5-1.2-3.8-2-7.7-2-11.8v-9.2z"></path>
                        </svg>
                      </div>
                      Account settings
                    </router-link>
                  </li>
                  <li v-if="getUserRole === 'ca' || getUserRole === 'ta' || getUserRole === 'la'">
                    <router-link :to="{name: 'billing-profile'}"
                                 class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                      <div class="app-header-login__item--icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16.031" viewBox="0 0 12 16.031">
                          <path fill="currentColor" d="M12.188.094a.473.473,0,0,1,.531-.047A.473.473,0,0,1,13,.5v15a.509.509,0,0,1-.812.406L11,14.5,9.313,15.906a.5.5,0,0,1-.625,0L7,14.5,5.313,15.906a.5.5,0,0,1-.625,0L3,14.5,1.813,15.906a.473.473,0,0,1-.531.047A.473.473,0,0,1,1,15.5V.5A.473.473,0,0,1,1.281.047a.473.473,0,0,1,.531.047L3,1.5,4.688.094a.5.5,0,0,1,.625,0L7,1.5,8.688.094a.5.5,0,0,1,.625,0L11,1.5ZM10.5,11.5A.442.442,0,0,0,11,11a.442.442,0,0,0-.5-.5h-7A.442.442,0,0,0,3,11a.442.442,0,0,0,.5.5Zm0-3A.442.442,0,0,0,11,8a.442.442,0,0,0-.5-.5h-7A.442.442,0,0,0,3,8a.442.442,0,0,0,.5.5Zm0-3A.442.442,0,0,0,11,5a.442.442,0,0,0-.5-.5h-7A.442.442,0,0,0,3,5a.442.442,0,0,0,.5.5Z" transform="translate(-1 0.016)"/>
                        </svg>
                      </div>
                      Billing profile
                    </router-link>
                  </li>
                  <li>
                    <router-link :to="{name: 'ca-users'}"
                                 v-if="getUserRole === 'ca' || getUserRole === 'ta' || getUserRole === 'la'"
                                 class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                      <div class="app-header-login__item--icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16">
                          <path fill="currentColor" d="M12.3-4.953A3.131,3.131,0,0,1,10-4a3.131,3.131,0,0,1-2.3-.953,3.131,3.131,0,0,1-.953-2.3,3.131,3.131,0,0,1,.953-2.3A3.131,3.131,0,0,1,10-10.5a3.131,3.131,0,0,1,2.3.953,3.131,3.131,0,0,1,.953,2.3A3.131,3.131,0,0,1,12.3-4.953ZM11.563-3a4.436,4.436,0,0,1,3.125,1.219A3.866,3.866,0,0,1,16,1.156a.788.788,0,0,1-.266.594.864.864,0,0,1-.609.25H4.875a.864.864,0,0,1-.609-.25A.788.788,0,0,1,4,1.156,3.866,3.866,0,0,1,5.313-1.781,4.436,4.436,0,0,1,8.438-3Zm6.2-6.734A2.407,2.407,0,0,1,16-9a2.407,2.407,0,0,1-1.766-.734A2.407,2.407,0,0,1,13.5-11.5a2.407,2.407,0,0,1,.734-1.766A2.407,2.407,0,0,1,16-14a2.407,2.407,0,0,1,1.766.734A2.407,2.407,0,0,1,18.5-11.5,2.407,2.407,0,0,1,17.766-9.734ZM5.75-7.25A4.224,4.224,0,0,0,6.781-4.5H.563a.524.524,0,0,1-.406-.172A.6.6,0,0,1,0-5.094,2.907,2.907,0,0,1,.8-7.141,2.561,2.561,0,0,1,2.75-8H4.688a2.75,2.75,0,0,1,1.125.25A2.689,2.689,0,0,0,5.75-7.25Zm.016-2.484A2.407,2.407,0,0,1,4-9a2.407,2.407,0,0,1-1.766-.734A2.407,2.407,0,0,1,1.5-11.5a2.407,2.407,0,0,1,.734-1.766A2.407,2.407,0,0,1,4-14a2.407,2.407,0,0,1,1.766.734A2.407,2.407,0,0,1,6.5-11.5,2.407,2.407,0,0,1,5.766-9.734ZM17.25-8a2.561,2.561,0,0,1,1.953.859A2.907,2.907,0,0,1,20-5.094a.6.6,0,0,1-.156.422.524.524,0,0,1-.406.172H13.219A4.224,4.224,0,0,0,14.25-7.25a3.835,3.835,0,0,0-.062-.5A2.75,2.75,0,0,1,15.313-8Z" transform="translate(0 14)"/>
                        </svg>
                      </div>
                      Users
                    </router-link>
                  </li>
                </ul>

              </li>
            </ul>
            <div class="app-header-right-menus">
              <ul class="app-header-login">
                <li class="has-dropdown"
                    :class="[{ 'opened border-1-70': menuShown === 'login', 'background-1-10': mobileMenuShown && menuShown === 'login' && !getDeviceType('desktop') }]"
                    @click.stop="">
                  <a href="#"
                     title="Open menu"
                     class="color-2-100 background-1-70-after"
                     @click.prevent="openSubmenu($event, 'login')">
                  <span class="app-header-login-icon-before sf-color-1-60">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                         viewBox="0 0 11.096 14.417" style="enable-background:new 0 0 11.096 14.417;" xml:space="preserve">
                      <circle id="XMLID_497_" class="st0" cx="5.548" cy="3.75" r="3.75"/>
                      <path id="XMLID_496_" class="st0" d="M1.002,14.417c-0.619,0-1.087-0.558-0.989-1.17c0.442-2.766,2.492-4.455,5.535-4.455
                        s5.093,1.69,5.535,4.455c0.098,0.611-0.37,1.17-0.989,1.17H1.002z"/>
                    </svg>
                  </span>
                    <span class="menu-text">My Account</span>
                    <span class="app-header-login-icon-after sf-color-1-60">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-down" class="svg-inline--fa fa-chevron-down fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                      <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
                    </svg>
                  </span>
                  </a>
                  <template v-if="getIsLoggedIn">
                    <ul class="app-header-login__dropdown border-1-70" style="left: unset; right: 0">
                      <li>
                        <router-link :to="{name: 'profile'}"
                                     class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                          <div class="app-header-login__item--icon">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="user-circle" class="svg-inline--fa fa-user-circle fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512">
                              <path fill="currentColor" d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm0 96c48.6 0 88 39.4 88 88s-39.4 88-88 88-88-39.4-88-88 39.4-88 88-88zm0 344c-58.7 0-111.3-26.6-146.5-68.2 18.8-35.4 55.6-59.8 98.5-59.8 2.4 0 4.8.4 7.1 1.1 13 4.2 26.6 6.9 40.9 6.9 14.3 0 28-2.7 40.9-6.9 2.3-.7 4.7-1.1 7.1-1.1 42.9 0 79.7 24.4 98.5 59.8C359.3 421.4 306.7 448 248 448z"></path>
                            </svg>
                          </div>
                          My profile
                        </router-link>
                      </li>
                      <li>
                        <router-link :to="{name: 'email-preferences'}"
                                     class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover">
                          <div class="app-header-login__item--icon">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="envelope" class="svg-inline--fa fa-envelope fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                              <path fill="currentColor" d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"></path>
                            </svg>
                          </div>
                          My email preferences
                        </router-link>
                      </li>
                      <li>
                        <a href="#"
                           class="app-header-login__item app-header-login__item--name has-icon color-2-100 background-1-20-hover" @click.prevent="authLogout">
                          <div class="app-header-login__item--icon">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="sign-out-alt" class="svg-inline--fa fa-sign-out-alt fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                              <path fill="currentColor" d="M497 273L329 441c-15 15-41 4.5-41-17v-96H152c-13.3 0-24-10.7-24-24v-96c0-13.3 10.7-24 24-24h136V88c0-21.4 25.9-32 41-17l168 168c9.3 9.4 9.3 24.6 0 34zM192 436v-40c0-6.6-5.4-12-12-12H96c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h84c6.6 0 12-5.4 12-12V76c0-6.6-5.4-12-12-12H96c-53 0-96 43-96 96v192c0 53 43 96 96 96h84c6.6 0 12-5.4 12-12z"></path>
                            </svg>
                          </div>
                          Logout
                        </a>
                      </li>
                    </ul>
                  </template>
                  <template v-else>
                    <router-link :to="{name: 'login'}" class="app-header-login__item color-2-100">Login</router-link>
                  </template>
                </li>
              </ul>
              <!--<div class="accessibility-trigger"
                   :class="[{ 'opened': accessibilityOpen }]">
                <a href="#"
                   v-bind:aria-expanded="accessibilityOpen"
                   title="Open accessibility"
                   @click.prevent.stop="openSubmenu($event, 'accessibility')">
                  <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="universal-access" class="svg-inline&#45;&#45;fa fa-universal-access fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path fill="currentColor" d="M256 48c114.953 0 208 93.029 208 208 0 114.953-93.029 208-208 208-114.953 0-208-93.029-208-208 0-114.953 93.029-208 208-208m0-40C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 56C149.961 64 64 149.961 64 256s85.961 192 192 192 192-85.961 192-192S362.039 64 256 64zm0 44c19.882 0 36 16.118 36 36s-16.118 36-36 36-36-16.118-36-36 16.118-36 36-36zm117.741 98.023c-28.712 6.779-55.511 12.748-82.14 15.807.851 101.023 12.306 123.052 25.037 155.621 3.617 9.26-.957 19.698-10.217 23.315-9.261 3.617-19.699-.957-23.316-10.217-8.705-22.308-17.086-40.636-22.261-78.549h-9.686c-5.167 37.851-13.534 56.208-22.262 78.549-3.615 9.255-14.05 13.836-23.315 10.217-9.26-3.617-13.834-14.056-10.217-23.315 12.713-32.541 24.185-54.541 25.037-155.621-26.629-3.058-53.428-9.027-82.141-15.807-8.6-2.031-13.926-10.648-11.895-19.249s10.647-13.926 19.249-11.895c96.686 22.829 124.283 22.783 220.775 0 8.599-2.03 17.218 3.294 19.249 11.895 2.029 8.601-3.297 17.219-11.897 19.249z"></path>
                  </svg>
                </a>
                <div class="front-header-capabilities border-1-70"
                     ref="accessibilityContainer"
                     v-bind:aria-expanded="accessibilityOpen"
                     :class="[{ 'opened': accessibilityOpen }]">
                  <div class="front-header-capabilities-inner">
                    <ul class="text-resize-controls">
                      <li><button class="text-resize-controls__button text-resize-controls__normal" id="accessibilityFontSize_0" title="Normal text size" @click.prevent="resizeText(0)">A</button></li>
                      <li><button class="text-resize-controls__button text-resize-controls__medium" id="accessibilityFontSize_1" title="Medium text size" @click.prevent="resizeText(1)">A</button></li>
                      <li><button class="text-resize-controls__button text-resize-controls__large" id="accessibilityFontSize_2" title="Big text size" @click.prevent="resizeText(2)">A</button></li>
                    </ul>
                    <div class="color-change-controls">
                      <button class="color-change-controls__button color-change-controls__normal" id="accessibilityColorPalette_0" title="Normal view" @click.prevent="changeVisibilityThem(0)"></button>
                      <button class="color-change-controls__button color-change-controls__contrast" id="accessibilityColorPalette_1" title="High contrast view" @click.prevent="changeVisibilityThem(1)"></button>
                      <button class="color-change-controls__button color-change-controls__dyslexia" id="accessibilityColorPalette_2" title="Dyslexia friendly view" @click.prevent="changeVisibilityThem(2)"></button>
                    </div>
                  </div>
                </div>
              </div>-->
            </div>
            <!-- END:.app-header-login -->
          </div>
        </div>
        <!-- END:.app-header-info__container -->
      </div>
      <!-- END:.myContainer -->
    </div>
    <!-- END:.app-header-info -->
    <!-- .app-header-search -->
    <!--<div class="app-header-search">-->
    <!--&lt;!&ndash; .myContainer &ndash;&gt;-->
    <!--<div class="myContainer">-->
    <!--<autocomplete-input />-->
    <!--</div>-->
    <!--&lt;!&ndash; END:.myContainer &ndash;&gt;-->
    <!--</div>-->
    <!-- END:.app-header-search -->
    <button @click="activateFeedback()" class="feedback-button" v-if="!hideFeedbackButton" aria-label="Send us your Feedback">
      <span aria-hidden="true">Send us your Feedback</span>
    </button>
  </header>
  <!-- END:.app-header -->
</template>

<script>
  import {mapGetters, mapActions} from 'vuex'
  import api from '@/api'

  export default {
    name: 'app-header',
    data () {
      return {
        menuShown: null,
        accessibilityOpen: false,
        notifications: [],
        accessibilityLinks: false,
        unreadNotifications: null,
        sponsors: [],
        impersonate: [],
        hideConsent: false,
        activeSection: null,
        customLogo: false,
        industriesAlts: '',
        windowWidth: window.innerWidth,
        mobileMenuShown: false,
        confOpened: null,
        accountExpiring: false,
        accountExpiringDate: false,
        hasActiveSubscription: false,
        hideFeedbackButton: false,
        paid: false
      }
    },
    computed: {
      ...mapGetters([
        'getIsLoggedIn',
        'getFirstName',
        'getLastName',
        'getUserRole',
        'getConsentRequired',
        'getImpersonatingUser',
        'isAdmin',
        'isRegularUser',
        'isLightUser',
        'getUserRole',
        'getConfData',
        'profile'
      ]),
      userDashboard () {
        if (this.isAdmin) {
          return 'admin'
        } else if (this.isRegularUser) {
          return 'user-app'
        } else {
          return 'knowledge-base'
        }
      }
    },
    methods: {
      ...mapActions([
        'authLogout',
        'getUserRoles',
        'resizeText',
        'changeVisibilityThem',
        'setFontSizeActive',
        'setColorPaletteActive',
        'initConfAction',
        'clearConfAction'
      ]),
      getFullServerPath (fileName) {
        return api.serverContenImages + fileName
      },
      getIndustryLogo () {
        let industryId = localStorage.getItem('userIndustry')
        if (!industryId) {
          return
        }
        let data = {
          id: industryId
        }
        this.$http.post(api.URLS.industry + '/logo', data, api.headersAuthSettings).then((res) => {
          this.customLogo = res.body.logo
        }).catch((err) => {
          console.log(err)
        })
      },
      hideMenuIfOpen () {
        if (this.menuShown) {
          this.menuShown = null
        }
        if (this.accessibilityOpen) {
          this.accessibilityOpen = false
        }
        if (this.activeSection) {
          this.activeSection = ''
        }
        if (this.mobileMenuShown) {
          this.mobileMenuShown = false
        }
      },
      openSubmenu (evt, menu) {
        if (menu === 'accessibility') {
          this.accessibilityOpen = !this.accessibilityOpen
          this.menuShown = null
          setTimeout(function () {
            let button = document.getElementById('reciteMe')
            if (button) {
              button.focus()
            }
          }, 100)
        } else if (menu === 'mobileMenu') {
          this.mobileMenuShown = !this.mobileMenuShown
        } else {
          if (this.menuShown === menu) {
            this.menuShown = null
            return
          }
          this.menuShown = menu
          this.activeSection = ''
          this.accessibilityOpen = false
          let current = evt.target
          let all = document.querySelectorAll('a')
          let currentIndex
          for (let i = 0; i < all.length; i++) {
            if (current.isEqualNode(all[i])) {
              currentIndex = i
            }
          }
          if (all[currentIndex + 1]) {
            setTimeout(function () {
              all[currentIndex + 1].focus()
            }, 100)
          }
        }
      },
      getNotifications () {
        let body = {
          offset: 0,
          limit: 3
        }
        this.$http.post(api.URLS.userNotifications, body, api.headersAuthSettings).then((res) => {
          this.unreadNotifications = res.body.unread > 0 ? res.body.unread : null
          let notifications = res.body.notifications
          let temp
          this.notifications = []
          for (let index in notifications) {
            let notification = notifications[index]
            let date = this.convertDate(notification.createdAt, '.')
            let group = this.notifications.find((item) => item.date === date)
            temp = {
              type: notification.type,
              id: notification.id,
              contentId: notification.contentId,
              companyId: notification.companyId,
              userFromId: notification.userFromId,
              message: notification.message,
              isRead: notification.isRead
            }
            if (group) {
              group.children.push(temp)
            } else {
              this.notifications.push({
                date: date,
                children: [{
                  type: notification.type,
                  id: notification.id,
                  contentId: notification.contentId,
                  companyId: notification.companyId,
                  userFromId: notification.userFromId,
                  isRead: notification.isRead,
                  message: notification.message
                }]
              })
            }
          }
        }).catch((err) => {
          if (err.status === 401) {
            this.authLogout()
          }
        })
      },
      markNotificationsRead (ids) {
        this.unreadNotifications = null
        let body = []
        if (ids) {
          body = ids
        }
        this.$http.post(api.URLS.userNotificationsRead, body, api.headersAuthSettings).then((res) => {
          this.getNotifications()
        }).catch((err) => console.log(err))
      },
      readNotification (item) {
        this.markNotificationsRead([item.id])
        // if (item.type === 1) {
        //   if (item.companyId && item.contentId) {
        //     let props = this.$router.resolve({
        //       name: 'edit-company',
        //       params: {
        //         id: item.companyId
        //       },
        //       query: {
        //         ttd: item.contentId
        //       }
        //     })
        //     this.$router.push(props.href)
        //   }
        // } else {
        //   if (item.userFromId) {
        //     let props = this.$router.resolve({
        //       name: 'edit-user',
        //       params: {
        //         id: item.userFromId
        //       }
        //     })
        //     this.$router.push(props.href)
        //   } else {
        //     let props = this.$router.resolve({
        //       name: 'consent-logs'
        //     })
        //     this.$router.push(props.href)
        //   }
        // }
      },
      updateNotifications () {
        let self = this
        self.getNotifications()
        setInterval(function () {
          self.getNotifications()
        }, 60000)
      },
      updateConsent () {
        let props = this.$router.resolve({
          name: 'give-consent'
        })
        this.$router.push(props.href)
      },
      discardConsent () {
        let body = {
          consent: 2
        }
        this.$http.put(api.URLS.updateConsent, body, api.headersAuthSettings).then(() => {
          this.$store.commit('changeConsent', 'false')
          let props = this.$router.resolve({
            name: 'profile'
          })
          location.href = props.href
        }).catch((err) => {
          console.log('err', err)
        })
      },
      removeImpersonate () {
        this.$store.commit('changeImpersonate', false)
      },
      convertDate (payload, delimiter) {
        if (!payload) return
        let date = new Date(payload)
        let fullYear = date.getFullYear()
        let month = date.getMonth() + 1
        let day = date.getDate()
        function toDoubleDigit (num) {
          return num.toString().length < 2 ? '0' + num : num.toString()
        }
        return toDoubleDigit(day) + delimiter + toDoubleDigit(month) + delimiter + fullYear
      },
      getTodayDate (text) {
        let date = new Date()
        let todayText = this.convertDate(date, '.')
        if (text === todayText) {
          return 'TODAY'
        } else {
          return text
        }
      },
      getCustomUserName () {
        let firstName = this.getFirstName
        let lastName = this.getLastName
        let fullName = ''
        if (firstName || lastName) {
          if (firstName) {
            fullName += firstName + ' '
          }
          if (lastName) {
            fullName += lastName.charAt(0) + '.'
          }
          return fullName
        } else {
          return 'My profile'
        }
      },
      skipTo (section) {
        let toThis = document.getElementsByClassName(section)
        if (toThis[0]) {
          setTimeout(function () {
            toThis[0].focus()
          }, 0)
        }
      },
      hasSection (section) {
        let toThis = document.getElementsByClassName(section)
        return !!toThis[0]
      },
      isPage (pages) {
        let route = this.$route.name
        let response = false
        if (pages.indexOf(route) > -1) {
          response = true
        }
        return response
      },
      checkAccess () {
        let userData = JSON.parse(localStorage.getItem('userData'))
        if (userData) {
          this.$http.put(api.URLS.profile, {}, api.headersAuthSettings).then((res) => {
            userData.id = res.body.id
            userData.userRole = res.body.role.slug
            userData.company = res.body.company ? res.body.company.id : null
            // if (res.body.password_changed === 1 && this.$route.name !== 'profile' && this.$route.name !== 'give-consent') {
            //   let props = this.$router.resolve({
            //     name: 'profile'
            //   })
            //   this.$router.push(props.href)
            // }
            if (res.body.consent_status !== 1 && res.body.role.slug === 'ca') {
              userData.userRole = 'sh'
              this.$store.commit('authLoginSetUserRole', 'sh')
            }
            if (res.body.consent_status === 1 && res.body.role.slug === 'ca') {
              userData.userRole = 'ca'
              this.$store.commit('authLoginSetUserRole', 'ca')
            }
            // if ((res.body.consent_status === 1 && res.body.role.slug === 'ca' && this.getUserRole === 'sh') ||
            //   (res.body.consent_status === 0 && res.body.role.slug === 'ca' && this.getUserRole === 'ca')) {
            //   location.href = location.href
            // }
            if (userData.firstName !== res.body.first_name || userData.lastName !== res.body.last_name) {
              userData.firstName = res.body.first_name
              userData.lastName = res.body.last_name
              this.$store.commit('changeAuthUserData', userData)
              localStorage.setItem('userData', JSON.stringify(userData))
            }
            let userIndustry = res.body.company && res.body.company.industry ? res.body.company.industry.id : 1
            localStorage.setItem('userIndustry', userIndustry)
            document.getElementById('app').setAttribute('data-industry', userIndustry)
            if (res.body.company) {
              localStorage.setItem('userCompanyName', res.body.company.name)
            }
            if (res.body.company && res.body.company.industry) {
              localStorage.setItem('userIndustryName', res.body.company.industry.name)
            }
            this.getSponsors()
            localStorage.setItem('userData', JSON.stringify(userData))
            this.$store.commit('changeAuthUserData', userData)
            if (userData.company) {
              this.$store.commit('authLoginSetCompany', userData.company)
            }
            this.impersonate = {
              'role': res.body.role.slug.toUpperCase(),
              'firstName': res.body.first_name,
              'lastName': res.body.last_name
            }
            let route = this.$route.name
            if (route === 'user-app' && res.body.role.slug === 'gm') {
              let props = this.$router.resolve({
                name: 'admin'
              })
              this.$router.push(props.href)
            }
          }).catch((err) => {
            console.log(err)
          })
        }
      },
      getSponsors () {
        let self = this
        let industryId = localStorage.getItem('userIndustry') ? localStorage.getItem('userIndustry') : 1
        this.$http.post(api.URLS.sponsors + '/' + industryId, {}, api.headersAuthSettings).then((res) => {
          self.sponsors = res.body.sponsors
          this.generateIndustriesAlts(self.sponsors)
          if (!self.sponsors.length) {
            this.$http.post(api.URLS.sponsors + '/1', {}, api.headersAuthSettings).then((res2) => {
              self.sponsors = res2.body.sponsors
              this.generateIndustriesAlts(self.sponsors)
            }).catch((err) => {
              console.log(err)
            })
          }
          localStorage.setItem('userIndustry', res.body.industry)
          document.getElementById('app').setAttribute('data-industry', res.body.industry)
        }).catch((err) => {
          console.log(err)
        })
      },
      generateIndustriesAlts (sponsors) {
        const alts = []
        for (let i = 0; i < sponsors.length; i++) {
          alts.push(sponsors[i].altImage)
        }
        this.industriesAlts = alts.join(', ')
      },
      getImagePath () {
        return api.serverURL + '/uploads/images/content/'
      },
      isBase64 (payload) {
        return payload.indexOf('data:') !== -1
      },
      getImage (payload) {
        if (!payload) {
          return ''
        }
        return this.isBase64(payload) ? 'url(' + payload + ')' : 'url(' + this.getImagePath() + payload + ')'
      },
      getImageFullPath (payload) {
        if (!payload) {
          return ''
        }
        return this.isBase64(payload) ? payload : this.getImagePath() + payload
      },
      getDeviceType (check) {
        let width = this.windowWidth
        let deviceName = ''
        if (width > 940) {
          deviceName = 'desktop'
        } else if (width > 640) {
          deviceName = 'tablet'
        } else {
          deviceName = 'mobile'
        }
        return check === deviceName
      },
      handleWindowResize (event) {
        this.windowWidth = event.currentTarget.innerWidth
      },
      activePage (path) {
        return location.pathname === path || location.pathname === path + '/'
      },
      activateFeedback () {
        this.confOpened = 'send-feedback'
        this.initConfAction({
          actionName: 'sendTasksFeedback',
          includeForm: true,
          customCancelButtonText: 'Cancel',
          customConfirmButtonText: 'Submit'
        })
      },
      submitFeedback () {
        const data = {
          text: this.getConfData.feedbackText,
          location: location.href,
          width: window.innerWidth,
          height: window.innerHeight
        }
        this.$http.put(`${api.URLS.task}/feedback`, data, api.headersAuthSettings).then(() => {
        }).catch(() => {
        })
      },
      setupExpiration () {
        if (!this.profile || !this.profile.company || !this.profile.company.subscription_end) {
          return
        }
        if (this.profile.company.is_temporary || this.profile.company.is_demo || this.profile.company.is_free) {
          this.accountExpiring = false
          return
        }
        const commitmentEnd = (new Date(this.profile.company.commitment_end)).setHours(0, 0)
        const subscriptionEnd = (new Date(this.profile.company.subscription_end)).setHours(0, 0)
        const diffTime = subscriptionEnd - new Date()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        if (diffDays <= 30) {
          this.accountExpiring = diffDays
          this.hasActiveSubscription = commitmentEnd && commitmentEnd > subscriptionEnd
          this.accountExpiringDate = this.convertDate(this.profile.company.subscription_end, '.')
        }
      },
      maybeHideFeedbackButtonNeeded () {
        this.hideFeedbackButton = this.$route.name === 'complete-profile' || this.$route.name === 'renew-subscription'
      }
    },
    mounted () {
      let self = this
      this.getUserRoles()
      setTimeout(() => {
        self.checkAccess()
        self.updateNotifications()
        self.getIndustryLogo()
      }, 2000)
      window.addEventListener('resize', this.handleWindowResize)
      document.addEventListener('click', this.hideMenuIfOpen)
      if (this.$route.name === 'give-consent') {
        this.hideConsent = true
      }
      this.paid = this.$route.query.paid || false
      this.maybeHideFeedbackButtonNeeded()
    },
    beforeDestroy () {
      window.addEventListener('resize', this.handleWindowResize)
    },
    destroyed () {
      document.addEventListener('click', this.hideMenuIfOpen)
    },
    watch: {
      'activeSection' (value) {
        if (value && value === 'notifications') {
          this.menuShown = null
          this.accessibilityOpen = false
        }
      },
      'getConfData.actionConfirmed' (value) {
        if (value) {
          if (this.confOpened === 'send-feedback') {
            this.submitFeedback()
            this.confOpened = null
            this.clearConfAction()
          }
        }
      },
      'profile' (value) {
        if (value) {
          this.setupExpiration()
        }
      },
      $route (from, to) {
        this.maybeHideFeedbackButtonNeeded()
      }
    }
  }

</script>

<style src="@/assets/scss/components/app-header.scss" lang="scss" scoped/>
<style>
  @media (min-width: 670px) and (max-width: 1000px) {
    .flex-space-between-center {
      width: 90%;
    }
  }
  @media screen and (max-width: 480px) {
    .front-header-tagline {
      display: none;
    }
  }
</style>



// WEBPACK FOOTER //
// AppHeader.vue?029e670f